import dayjs from 'dayjs';
import { useState } from 'react';
import { Box } from '@mui/material';
import MultiAgentDashboard from '../../components/dashboard/MultiAgentDashboard/MultiAgentDashboard';
import Loading from '../../components/shared/Loading/Loading';
import Page from '../../components/shared/Page/Page';
import Tabs from '../../components/shared/Tabs/Tabs';
import WelcomeScreen from '../../components/welcome/WelcomeScreen';
import useAuth from '../../hooks/useAuth';
import { useDashboardAgents } from '../../hooks/queries/useDashboardAgents';
import { useDashboardChatAgents } from '../../hooks/queries/useDashboardChatAgents';
import { useTranslation } from '../../hooks/useTranslation';
import { AgentType } from '../../interfaces/Agent';
import { DateRangeDate } from '../../interfaces/Analytics';
import { DeviceWidth } from '../../interfaces/App';
import HorizontalStack from '../../styles/HorizontalStack';

const Dashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  // Shared date range state for both call and chat agent dashboards
  const [dateRange, setDateRange] = useState<[DateRangeDate, DateRangeDate]>([
    dayjs().subtract(14, 'day'),
    dayjs(),
  ]);

  const { data: callAgents, isLoading: callAgentsLoading } =
    useDashboardAgents(!!currentUser);
  const { data: chatAgents, isLoading: chatAgentsLoading } =
    useDashboardChatAgents(!!currentUser);

  const agentsCount = callAgents.length + chatAgents.length;

  if (callAgentsLoading || chatAgentsLoading || !currentUser)
    return <Loading />;

  const dashboardCardSx = {
    width: '100%',
    p: { xs: 1.25, md: 2 },
    bgcolor: '#FFFFFF',
    borderRadius: { xs: 3, md: 4 },
    border: '1px solid rgba(15, 23, 42, 0.06)',
    boxShadow: '0px 10px 28px rgba(15, 23, 42, 0.08)',
  };

  return (
    <Page title="Elora | Dashboard" description="Build your AI agents!">
      {agentsCount > 0 ? (
        <HorizontalStack maxWidth={DeviceWidth.LG}>
          <Tabs
            labels={[t('Call Agents'), t('Chat Agents')]}
            contents={[
              <Box key="call-agents-dashboard-card" sx={{ mt: 2 }}>
                <Box sx={dashboardCardSx}>
                  <MultiAgentDashboard
                    key="call-agents"
                    type={AgentType.CALL}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                  />
                </Box>
              </Box>,
              <Box key="chat-agents-dashboard-card" sx={{ mt: 2 }}>
                <Box sx={dashboardCardSx}>
                  <MultiAgentDashboard
                    key="chat-agents"
                    type={AgentType.CHAT}
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                  />
                </Box>
              </Box>,
            ]}
          />
        </HorizontalStack>
      ) : (
        <HorizontalStack>
          <WelcomeScreen />
        </HorizontalStack>
      )}
    </Page>
  );
};

export default Dashboard;
